import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, Info, TrendingUp } from "lucide-react";

const TIERS = [
  {
    key: "startup",
    label: "Startup",
    range: "< $120K / mo",
    rangeMin: 0,
    rangeMax: 120000,
    priceLabel: "$1,200",
    priceSub: "/mo flat",
    description: "Predictable AI cost for smaller practices — no percentage of collections.",
  },
  {
    key: "growth",
    label: "Growth",
    range: "$120K – $200K / mo",
    rangeMin: 120000,
    rangeMax: 200000,
    priceLabel: "2%",
    priceSub: "of collections",
    description: "As you grow, AI cost scales with you — just a small share.",
  },
  {
    key: "enterprise",
    label: "Enterprise",
    range: "$200K+ / mo",
    rangeMin: 200000,
    rangeMax: Infinity,
    priceLabel: "1.65%",
    priceSub: "of collections",
    description: "Volume discount — the largest practices get the lowest rate.",
  },
];

const pmsCore = [
  "Smart Scheduling",
  "Clinical Charting",
  "Clinical Notes",
  "Document Management",
  "Digital Imaging",
  "Revenue Analytics",
];

const pmsAI = [
  "Asha AI Co-Pilot",
  "AI Treatment Planner",
  "AI Scribe",
  "AI Revenue Intelligence",
];

const frontOfficeAgents = [
  "Amy — Receptionist",
  "Leena — Schedule Optimizer",
  "Stephanie — Hygiene Recall",
  "Kim — Case Acceptance",
  "Elise — Patient Experience",
  "Morgan — Patient Intake",
  "Trisha — Practice Manager",
  "Joseph — Fax Manager",
];

const backOfficeAgents = [
  "Amanda — Insurance Verification",
  "Ariel — Claim Submission",
  "Alayna — Claim Scrubbing",
  "Sarah — Denial Management",
  "Alisha — EOB Posting",
  "Kianna — Patient Billing",
  "Julia — AR & Collections",
  "Laura — Insurance Calls",
];

const formatCurrency = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const FeatureGroup = ({ title, items, accent }) => (
  <div className="flex flex-col">
    <p className="text-[12px] font-bold text-slate-500 uppercase tracking-wider mb-2.5">
      {title}
    </p>
    <div className="space-y-1.5">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <Check className={`w-3.5 h-3.5 ${accent} shrink-0 mt-[3px]`} />
          <span className="text-[13px] text-slate-700 leading-snug">{item}</span>
        </div>
      ))}
    </div>
  </div>
);

const AshaPMSPricingSlide = () => {
  const [collections, setCollections] = useState(150000);

  const activeTier = useMemo(() => {
    return (
      TIERS.find((t) => collections >= t.rangeMin && collections < t.rangeMax) ||
      TIERS[TIERS.length - 1]
    );
  }, [collections]);

  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      {/* Ambient blue gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #93c5fd 50%, #dbeafe 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #bfdbfe 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1120px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-5"
        >
          <h2 className="text-3xl lg:text-[42px] font-bold text-slate-900 tracking-tight leading-tight">
            The only PMS{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              built for AI from the ground up.
            </span>
          </h2>
        </motion.div>

        {/* Collections selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="w-full max-w-[580px] mx-auto mb-5"
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Your monthly collections
            </p>
          </div>
          <div className="flex items-baseline justify-center gap-2 mb-3">
            <span className="text-3xl font-extrabold text-slate-900 tracking-tight tabular-nums">
              {formatCurrency(collections)}
            </span>
            <span className="text-sm text-slate-400">/mo</span>
          </div>
          <input
            type="range"
            min={40000}
            max={500000}
            step={5000}
            value={collections}
            onChange={(e) => setCollections(Number(e.target.value))}
            className="w-full accent-blue-600 cursor-pointer"
          />
          <div className="flex justify-between mt-1.5 text-[10px] text-slate-400 font-medium">
            <span>$40K</span>
            <span className="text-blue-500">$120K</span>
            <span className="text-blue-600">$200K</span>
            <span>$500K</span>
          </div>
        </motion.div>

        {/* Two-section pricing card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="w-full rounded-[20px] overflow-hidden shadow-[0_8px_60px_-12px_rgba(0,0,0,0.08)] border border-white/80 bg-white/95"
        >
          {/* Accent line */}
          <div
            className="h-[3px]"
            style={{
              background: "linear-gradient(90deg, #3b82f6 0%, #6366f1 50%, #3b82f6 100%)",
            }}
          />

          {/* Tier header — inside the card */}
          <motion.div
            key={activeTier.key}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="px-7 py-3.5 border-b border-slate-100 flex items-center justify-center gap-3 bg-gradient-to-r from-blue-50/60 via-white to-indigo-50/60"
          >
            <p className="text-[11px] font-bold text-blue-500 uppercase tracking-widest">
              Your Plan
            </p>
            <span className="w-1 h-1 rounded-full bg-slate-300" />
            <h3 className="text-[22px] font-extrabold tracking-tight leading-none bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              {activeTier.label}
            </h3>
          </motion.div>

          <div className="grid grid-cols-2 relative">
            {/* Bundled pricing indicator — sits over the divider at price row */}
            <div
              aria-hidden
              className="absolute left-1/2 -translate-x-1/2 top-[72px] z-10 w-11 h-11 rounded-full bg-white border border-blue-200 shadow-[0_4px_14px_-3px_rgba(59,130,246,0.3)] flex items-center justify-center"
            >
              <span className="text-2xl font-bold text-blue-600 leading-none">+</span>
            </div>

            {/* LEFT — Asha PMS / $1,600 */}
            <div className="p-7 border-r border-slate-100">
              <div className="mb-5 text-center">
                <p className="text-base font-bold text-blue-600 uppercase tracking-wider mb-2">
                  Asha PMS
                </p>
                <div className="flex items-baseline gap-1 justify-center">
                  <span className="text-5xl font-extrabold tracking-tight text-slate-900">
                    $1,600
                  </span>
                  <span className="text-sm font-medium text-slate-400">/mo</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <FeatureGroup
                  title="PMS Core"
                  items={pmsCore}
                  accent="text-blue-500"
                />
                <FeatureGroup
                  title="AI-Native Features"
                  items={pmsAI}
                  accent="text-indigo-500"
                />
              </div>
            </div>

            {/* RIGHT — AI Workforce / tiered */}
            <div className="p-7 bg-gradient-to-br from-blue-50/40 via-white to-indigo-50/30">
              <div className="mb-5 text-center">
                <p className="text-base font-bold text-indigo-600 uppercase tracking-wider mb-2">
                  Front Office AI Agents + Back Office AI Agents
                </p>
                <div className="flex items-baseline gap-1 justify-center">
                  <span className="text-5xl font-extrabold tracking-tight text-slate-900">
                    {activeTier.priceLabel}
                  </span>
                  <span className="text-sm font-medium text-slate-400">{activeTier.priceSub}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <FeatureGroup
                  title="8 Front Office AI Agents"
                  items={frontOfficeAgents}
                  accent="text-blue-500"
                />
                <FeatureGroup
                  title="8 Back Office AI Agents"
                  items={backOfficeAgents}
                  accent="text-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-slate-100/80 px-8 py-3 flex items-center justify-between gap-4 bg-slate-50/50">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500" />
              <span className="text-[13.5px] font-medium text-slate-600">
                <strong className="text-slate-700">150,000 AI credits</strong> included &middot;
                overage at <strong className="text-slate-700">2.3¢</strong> per 1,000 AI credits
              </span>
            </div>
            <div className="flex items-center gap-2 text-[13.5px] text-slate-500">
              <Info className="w-3.5 h-3.5" />
              <span>AI runs natively on Asha PMS — no integration fees</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AshaPMSPricingSlide;
